"use client"

import { addToCartEventBus } from "@/lib/data/cart-event-bus"
import { addToCartBulk } from "@/lib/data/cart"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { HttpTypes, StoreProduct } from "@medusajs/types"
import { IconButton, Input, Select } from "@medusajs/ui"
import Button from "@/modules/common/components/button"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import { BoltSolid, MinusMini, PlusMini } from "@medusajs/icons"
import { toast } from "@medusajs/ui"
import { useEffect, useRef, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import QuantityDeliveryEstimate from "../quantity-delivery-estimate"
import { WarehouseMetadata } from "@/lib/data/inventory"

const ProductVariantsTable = ({
  product,
  region,
  warehouseStock,
  warehouseMetadata,
}: {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  warehouseStock?: Record<string, number>
  warehouseMetadata?: Record<string, WarehouseMetadata>
}) => {
  const [isAdding, setIsAdding] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    product.variants?.[0]?.id ?? ""
  )
  const [quantity, setQuantity] = useState(1)
  const router = useRouter()
  const { countryCode } = useParams()
  const hasTrackedInitialVariantRef = useRef(false)

  const hasMultipleVariants =
    product.variants && product.variants.length > 1 &&
    product.variants.some((v) =>
      v.options?.some((o) => o.value !== "Default option value")
    )

  useEffect(() => {
    if (product.variants?.[0]?.id && !selectedVariantId) {
      setSelectedVariantId(product.variants[0].id)
    }
  }, [product.variants, selectedVariantId])

  const selectedVariant = product.variants?.find((v) => v.id === selectedVariantId)
  const totalQuantity = quantity

  useEffect(() => {
    if (!selectedVariant) {
      return
    }

    if (!hasTrackedInitialVariantRef.current) {
      hasTrackedInitialVariantRef.current = true
      return
    }

    captureAnalyticsEvent("product_variant_selected", {
      product_id: product.id,
      product_title: product.title,
      variant_id: selectedVariant.id,
      variant_title: selectedVariant.title,
      quantity,
    })
  }, [product.id, product.title, quantity, selectedVariant])

  const handleQuantityChange = (delta: number) => {
    setQuantity((prev) => Math.max(1, prev + delta))
  }

  const handleAddToCart = async () => {
    if (!selectedVariant) return
    setIsAdding(true)

    addToCartEventBus.emitCartAdd({
      lineItems: [
        {
          productVariant: { ...selectedVariant, product },
          quantity,
        },
      ],
      regionId: region.id,
    })

    setIsAdding(false)
  }

  const handleBuyNow = async () => {
    if (!selectedVariant || !countryCode) return
    setIsAdding(true)
    try {
      await addToCartBulk({
        lineItems: [{ variant_id: selectedVariant.id, quantity }],
        countryCode: countryCode as string,
      })
      captureAnalyticsEvent("checkout_started", {
        entry_point: "buy_now",
        product_id: product.id,
        product_title: product.title,
        variant_id: selectedVariant.id,
        quantity,
      })
      router.push(`/${countryCode}/products/${product.handle}?express=true`)
    } catch {
      toast.error("Napaka pri dodajanju v košarico")
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Variant SKU (šifra) - shown for selected variant */}
      {selectedVariant?.sku && (
        <div className="text-sm text-neutral-600">
          <span className="font-medium text-neutral-700">Šifra:</span>{" "}
          <span data-testid="product-variant-sku">{selectedVariant.sku}</span>
        </div>
      )}

      {/* Variant selector - only for multi-variant products */}
      {hasMultipleVariants && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-neutral-700">
            Izberite različico
          </label>
          <Select
            value={selectedVariantId}
            onValueChange={setSelectedVariantId}
          >
            <Select.Trigger>
              <Select.Value placeholder="Izberite različico" />
            </Select.Trigger>
            <Select.Content>
              {product.variants?.map((variant) => {
                const optionLabel = variant.options
                  ?.filter((o) => o.value !== "Default option value")
                  .map((o) => o.value)
                  .join(" / ")
                return (
                  <Select.Item key={variant.id} value={variant.id}>
                    {optionLabel || variant.title || `Varianta`}
                  </Select.Item>
                )
              })}
            </Select.Content>
          </Select>
        </div>
      )}

      {/* Quantity counter + Add to cart row */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        {/* Simple +/- quantity counter */}
        <div className="flex items-center gap-2 border border-neutral-200 rounded-full px-2 py-1 w-fit">
          <IconButton
            onClick={() => handleQuantityChange(-1)}
            className="rounded-full hover:bg-neutral-100"
            variant="transparent"
            disabled={quantity <= 1}
          >
            <MinusMini />
          </IconButton>
          <Input
            value={quantity}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10)
              if (!isNaN(val) && val >= 1) setQuantity(val)
            }}
            type="number"
            min={1}
            className="w-14 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none border-0 bg-transparent"
          />
          <IconButton
            onClick={() => handleQuantityChange(1)}
            className="rounded-full hover:bg-neutral-100"
            variant="transparent"
          >
            <PlusMini />
          </IconButton>
        </div>

        {/* Add to cart - secondary, subtle outline */}
        <Button
          onClick={handleAddToCart}
          variant="secondary"
          className="flex-1 sm:flex-initial !bg-transparent !text-neutral-600 !border !border-neutral-300 hover:!bg-neutral-50 hover:!border-neutral-400 h-12 md:h-10 text-base md:text-sm min-w-[180px]"
          isLoading={isAdding}
          disabled={!selectedVariant}
          data-testid="add-product-button"
        >
          <ShoppingBag
            className="mr-2"
            fill={selectedVariant ? "currentColor" : "none"}
          />
          Dodaj v košarico
        </Button>
      </div>

      {/* Buy now - primary, emphasized */}
      <Button
        onClick={handleBuyNow}
        variant="primary"
        className="w-full h-12 md:h-10 text-base md:text-sm !bg-navy hover:!bg-navy/90 !text-white !border-0"
        data-testid="buy-now-button"
        isLoading={isAdding}
        disabled={!selectedVariant}
      >
        <BoltSolid className="mr-2 w-4 h-4" />
        Kupi zdaj
      </Button>

      {/* Delivery Estimate */}
      {totalQuantity > 0 && selectedVariant && (
        <div>
          <QuantityDeliveryEstimate
            product={product}
            requestedQuantity={totalQuantity}
            className="max-w-md"
            warehouseStock={warehouseStock}
            warehouseMetadata={warehouseMetadata}
          />
        </div>
      )}
    </div>
  )
}

export default ProductVariantsTable
